<template>
    <section class="relative min-h-screen flex flex-col items-center justify-center text-center overflow-hidden pt-20 pb-12 sm:pt-24 sm:pb-14 lg:pt-28">

        <!-- Decorative ring -->
        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div class="w-[600px] h-[600px] sm:w-[900px] sm:h-[900px] rounded-full border opacity-5 animate-spin-slow"
                 style="border-color: rgba(167,93,237,0.8);"></div>
            <div class="absolute w-[400px] h-[400px] sm:w-[600px] sm:h-[600px] rounded-full border opacity-8 animate-spin-reverse"
                 style="border-color: rgba(232,121,249,0.5);"></div>
        </div>

        <div class="relative z-10 w-full px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">

            <!-- Headline -->
            <div class="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                <h1 class="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[5.5rem] font-black leading-[0.88] tracking-tighter">
                    <span class="text-white block hero-fade-up" style="--d:80ms">
                        {{ t('heroProfessional') }}
                    </span>
                    <span class="block hero-fade-up font-black" style="--d:200ms;background:linear-gradient(135deg,#7c3aed 0%,#a75ded 40%,#e879f9 80%,#c084fc 100%);-webkit-background-clip:text;background-clip:text;color:transparent;">
                        {{ t('heroWeb3Finance') }}
                    </span>
                    <span class="text-white/80 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold block hero-fade-up" style="--d:340ms">
                        {{ t('heroBeyondBoundaries') }}
                    </span>
                </h1>

                <p class="text-base sm:text-lg lg:text-xl leading-relaxed text-white/65 font-light hero-fade-up max-w-2xl mx-auto" style="--d:460ms">
                    {{ t('heroTagline') }}
                </p>
            </div>

            <!-- CTA Buttons -->
            <div class="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 hero-fade-up" style="--d:580ms">

                <template v-if="!isAuthenticated">
                    <button
                        class="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-base sm:text-lg border-2 transition-all duration-300 hover:scale-[1.04]"
                        style="border-color: rgba(167,93,237,0.5); color: #c084fc; background: rgba(167,93,237,0.08);">
                        <svg class="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Watch
                    </button>
                </template>

                <template v-else>
                </template>
            </div>

            <!-- Key Features -->
            <div class="w-full mb-10">
                <KeyFeatures />
            </div>

            <!-- Dashboard Preview -->
            <div v-if="showDashboardPreview" class="relative w-full mx-auto h-[460px] sm:h-[580px] lg:h-[680px] hero-fade-up" style="--d:940ms">
                <DashboardPreview
                    :portfolio-value="animatedPortfolioValue"
                    :daily-yield="animatedDailyYield"
                    :portfolio-growth="portfolioGrowth"
                    :trend-percentage="trendPercentage"
                    :last-update="lastUpdateTime"
                    :staking-rewards="stakingRewards"
                    :stats="animatedStats"
                    :show-live-opportunities="page.props.showLiveOpportunities !== false"
                    :show-platform-stats="page.props.showPlatformStats !== false"
                    :show-portfolio-overview="page.props.showPortfolioOverview !== false"
                />
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { usePage, Link } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'
import KeyFeatures from "@/Components/Frontend/KeyFeatures.vue"
import DashboardPreview from "@/Components/Frontend/DashboardPreview.vue"

defineProps({ cryptoData: { type: Array, default: () => [] } })

const page = usePage()
const { t } = useTranslation()
const authUser        = computed(() => page.props.auth?.user || null)
const isAuthenticated = computed(() => !!authUser.value)

const showDashboardPreview = computed(() =>
    page.props.showLiveOpportunities !== false ||
    page.props.showPlatformStats !== false ||
    page.props.showPortfolioOverview !== false
)

const walletConnected = ref(false)
const walletAddress   = ref('')
const portfolioValue  = ref(247500)
const dailyYield      = ref(1230)
const portfolioGrowth = ref(12.5)
const trendPercentage = ref(24.7)
const lastUpdateTime  = ref(2)

const animatedPortfolioValue = ref(0)
const animatedDailyYield     = ref(0)

const stakingRewards = reactive({ amount: '2.847', apy: '4.2' })
const animatedStats  = reactive({ tvl: 0, users: 0, transactions: 0, partners: 0 })

const animateCounters = () => {
    const targets = { tvl: 1250, users: 47, transactions: 892, partners: 150 }
    const steps = 100
    Object.keys(targets).forEach(key => {
        let current = 0
        const increment = targets[key] / steps
        const timer = setInterval(() => {
            current += increment
            if (current >= targets[key]) { animatedStats[key] = targets[key]; clearInterval(timer) }
            else animatedStats[key] = Math.floor(current * 10) / 10
        }, 3000 / steps)
    })
}

const animatePortfolioValues = () => {
    let pCurrent = 0, yCurrent = 0
    const pInc = portfolioValue.value / 60, yInc = dailyYield.value / 60
    const timer = setInterval(() => {
        pCurrent += pInc; yCurrent += yInc
        if (pCurrent >= portfolioValue.value) {
            animatedPortfolioValue.value = portfolioValue.value
            animatedDailyYield.value     = dailyYield.value
            clearInterval(timer)
        } else {
            animatedPortfolioValue.value = Math.floor(pCurrent)
            animatedDailyYield.value     = Math.floor(yCurrent)
        }
    }, 2000 / 60)
}

let updateIntervalId = null
onMounted(() => {
    if (page.props.auth?.user?.wallet_address) {
        walletConnected.value = true
        walletAddress.value   = page.props.auth.user.wallet_address
    }
    setTimeout(() => { animateCounters(); animatePortfolioValues() }, 800)
})
onUnmounted(() => { if (updateIntervalId) clearInterval(updateIntervalId) })
</script>

<style scoped>
/* Entrance animation */
.hero-fade-up {
    opacity: 0;
    animation: heroUp 0.75s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: var(--d, 0ms);
}
@keyframes heroUp {
    from { opacity:0; transform: translateY(32px); }
    to   { opacity:1; transform: none; }
}

/* Slow spin rings */
.animate-spin-slow    { animation: spinSlow 30s linear infinite; }
.animate-spin-reverse { animation: spinSlow 22s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
    .hero-fade-up, .animate-spin-slow, .animate-spin-reverse { animation: none !important; opacity: 1; }
}
</style>
